import React from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuCircleCheck, LuClock, LuFileUp, LuListChecks, LuPaperclip, LuPlus, LuSend, LuTreePalm, LuX } from 'react-icons/lu';
import Badge from '../../../../../components/ui/Badge';
import { useTranslation } from '../../../../../i18n/LanguageContext';
import { abs } from '../../engine/stage';
import { Abs, Btn, CARD, Field, SectionHead, Segmented, TILE } from '../kit';

// ── İzin Talep Et ───────────────────────────────────────────────────────
export const LEAVE = (top) => ({
  card: { x: 256, y: top + 60, w: 1000, h: 600 },
  types: { x: 280, y: top + 102, w: 960, h: 58 },
  type: (index) => ({ x: 280 + index * 324, y: top + 102, w: 312, h: 58 }),
  balance: { x: 280, y: top + 176, w: 952, h: 54 },
  balanceTile: (index) => ({ x: 280 + index * 241, y: top + 176, w: 229, h: 54 }),
  start: { x: 280, y: top + 272, w: 470, h: 42 },
  end: { x: 762, y: top + 272, w: 470, h: 42 },
  days: { x: 280, y: top + 352, w: 470, h: 42 },
  travel: { x: 762, y: top + 352, w: 470, h: 42 },
  reason: { x: 280, y: top + 446, w: 470, h: 42 },
  address: { x: 762, y: top + 446, w: 470, h: 42 },
  report: { x: 280, y: top + 526, w: 128, h: 36 },
  submit: { x: 1000, y: top + 598, w: 232, h: 42 },
});

const TYPES = ['yillik', 'mazeret', 'raporlu'];

/**
 * İzin Talep Et. state: { type, filled (0-4), focus, pressed, reportChosen }
 * filled: 1 = dates, 2 = travel days, 3 = reason + address
 */
export const LeaveFormPane = ({ top, headTop, state }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.leave.form.${key}`, vars);
  const L = LEAVE(top);
  const type = state.type || 'yillik';
  const filled = state.filled || 0;
  const balance = [['entitlement', 14], ['used', 3], ['reserved', 0], ['remaining', 11]];
  return (
    <>
      <SectionHead top={headTop ?? top} icon={LuTreePalm} gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30" title={label('title')} description={label('description')} />
      <Abs rect={L.card} className={CARD} />
      <span style={{ left: 282, top: top + 80 }} className="tw-absolute tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('type')} <span className="tw-text-rose-500">*</span></span>
      {TYPES.map((key, index) => (
        <Abs key={key} rect={L.type(index)} className={`tw-flex tw-flex-col tw-justify-center tw-rounded-2xl tw-border tw-px-4 tw-transition-colors tw-duration-300 ${type === key ? 'tw-border-teal-400/70 tw-bg-teal-500/[0.07]' : 'tw-border-slate-200 tw-bg-white/70 dark:tw-border-white/10 dark:tw-bg-white/[0.03]'}`}>
          <span className={`tw-text-sm tw-font-semibold ${type === key ? 'tw-text-teal-800 dark:tw-text-teal-200' : 'tw-text-slate-800 dark:tw-text-slate-100'}`}>{t(`profilePage.leave.types.${key}`)}</span>
          <span className="tw-text-[11px] tw-text-slate-500">{label(`typeHints.${key}`)}</span>
        </Abs>
      ))}
      {type === 'yillik' && balance.map(([key, value], index) => (
        <Abs key={key} rect={L.balanceTile(index)} className={`tw-px-4 tw-py-2 ${TILE}`}>
          <p className={`tw-text-lg tw-font-semibold tw-leading-tight tw-tabular-nums ${key === 'remaining' ? 'tw-text-emerald-600' : 'tw-text-slate-900 dark:tw-text-white'}`}>{key === 'reserved' && state.submitted ? 3 : key === 'remaining' && state.submitted ? 8 : value}</p>
          <p className="tw-text-[11px] tw-text-slate-500">{label(`balance.${key}`)}</p>
        </Abs>
      ))}
      <Field rect={L.start} label={label('start')} required value={filled >= 1 ? '19.10.2026' : ''} placeholder="gg.aa.yyyy" focused={state.focus === 'start'} />
      <Field rect={L.end} label={label('end')} required value={filled >= 1 ? '21.10.2026' : ''} placeholder="gg.aa.yyyy" focused={state.focus === 'end'} />
      <Field rect={L.days} label={label('days')} value={filled >= 1 ? '3' : ''} hint={label('daysHint')} className={filled >= 1 ? 'tw-transition-colors' : ''} />
      <Field rect={L.travel} label={label('travelDays')} value={filled >= 2 ? '0,5' : ''} placeholder="0" focused={state.focus === 'travel'} hint={label('travelDaysHint')} />
      <Field rect={L.reason} label={label('reason')} value={filled >= 3 ? t('userGuide.stage.leaveReason') : ''} placeholder={label('reasonPlaceholder')} focused={state.focus === 'reason'} />
      <Field rect={L.address} label={label('address')} value={filled >= 3 ? 'İzmir' : ''} placeholder={label('addressPlaceholder')} focused={state.focus === 'address'} hint={label('addressHint')} />
      <span style={{ left: 282, top: top + 504 }} className="tw-absolute tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('report')} <span className="tw-font-normal tw-text-slate-400">· {label('reportHint')}</span></span>
      <Btn rect={L.report} icon={LuFileUp}>{label('chooseReport')}</Btn>
      {state.reportChosen && (
        <span style={{ left: 420, top: top + 530 }} className="tw-absolute tw-inline-flex tw-h-7 tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-teal-500/10 tw-px-3 tw-text-xs tw-font-semibold tw-text-teal-800 dark:tw-text-teal-200">
          <LuPaperclip aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />rapor.pdf<LuX aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        </span>
      )}
      <span style={{ left: 282, top: top + 608, width: 700 }} className="tw-absolute tw-text-xs tw-text-slate-500">{label('approvalNote')}</span>
      <Btn rect={L.submit} variant="primary" icon={LuSend} pressed={state.pressed} busy={state.sending}>{state.sending ? label('sending') : label('submit')}</Btn>
    </>
  );
};

LeaveFormPane.propTypes = { top: PropTypes.number.isRequired, headTop: PropTypes.number, state: PropTypes.object.isRequired };

// ── İzin Taleplerim ─────────────────────────────────────────────────────
export const LEAVE_LIST = (top) => ({
  newRequest: { x: 1132, y: top + 4, w: 124, h: 34 },
  filter: { x: 256, y: top + 66, w: 452, h: 36 },
  filterWidths: [86, 120, 120, 114],
  card: (index) => ({ x: 256, y: top + 118 + index * 206, w: 1000, h: 194 }),
  note: (index) => ({ x: 280, y: top + 118 + index * 206 + 128, w: 952, h: 50 }),
});

const LEAVE_ROWS = [
  { type: 'yillik', status: 'PENDING', start: '19.10.2026', end: '21.10.2026', days: 3, travel: 0.5, sent: '11.10.2026' },
  { type: 'yillik', status: 'APPROVED', start: '07.07.2026', end: '09.07.2026', days: 3, travel: 0, sent: '20.06.2026', note: 'noteApproved' },
  { type: 'mazeret', status: 'REJECTED', start: '12.05.2026', end: '12.05.2026', days: 1, travel: 0, sent: '05.05.2026', note: 'noteRejected' },
];
const STATUS_TONE = { PENDING: ['warning', LuClock], APPROVED: ['success', LuCircleCheck], REJECTED: ['danger', LuX] };

export const LeaveListPane = ({ top, state }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.leave.list.${key}`, vars);
  const L = LEAVE_LIST(top);
  const filter = state.filter || 'ALL';
  const rows = filter === 'ALL' ? LEAVE_ROWS : LEAVE_ROWS.filter((row) => row.status === filter);
  const count = (status) => LEAVE_ROWS.filter((row) => row.status === status).length;
  return (
    <>
      <SectionHead top={top} icon={LuListChecks} gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30" title={label('title')} description={label('description')} />
      <Btn rect={L.newRequest} icon={LuPlus} className="tw-text-xs">{label('newRequest')}</Btn>
      <Segmented
        rect={L.filter}
        value={filter}
        small
        options={[
          { value: 'ALL', label: `${label('all')} (${LEAVE_ROWS.length})`, w: L.filterWidths[0] },
          { value: 'PENDING', label: `${t('profilePage.leave.status.PENDING')} (${count('PENDING')})`, w: L.filterWidths[1] },
          { value: 'APPROVED', label: `${t('profilePage.leave.status.APPROVED')} (${count('APPROVED')})`, w: L.filterWidths[2] },
          { value: 'REJECTED', label: `${t('profilePage.leave.status.REJECTED')} (${count('REJECTED')})`, w: L.filterWidths[3] },
        ]}
      />
      {rows.slice(0, 3).map((row, index) => {
        const [tone, Icon] = STATUS_TONE[row.status];
        const rect = L.card(index);
        return (
          <Abs key={`${row.status}-${row.start}`} rect={rect} className={`tw-p-5 ${CARD} ${state.highlight === index ? 'tw-ring-2 tw-ring-teal-400/60' : ''}`}>
            <div className="tw-flex tw-items-start tw-justify-between">
              <div>
                <p className="tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{t(`profilePage.leave.types.${row.type}`)}</p>
                <p className="tw-text-xs tw-text-slate-500">{label('sentOn', { date: row.sent })} · {label('sourceRequest')}</p>
              </div>
              <Badge tone={tone}><Icon aria-hidden="true" className="tw-h-3 tw-w-3" />{t(`profilePage.leave.status.${row.status}`)}</Badge>
            </div>
            <div className="tw-mt-3 tw-grid tw-grid-cols-4 tw-gap-3">
              {[['start', row.start], ['end', row.end], ['days', label('dayCount', { count: row.days })], ['travelDays', label('dayCount', { count: row.travel })]].map(([key, value]) => (
                <span key={key} className={`tw-px-3 tw-py-1.5 ${TILE}`}>
                  <span className="tw-block tw-text-[11px] tw-text-slate-500">{label(key)}</span>
                  <span className="tw-block tw-text-sm tw-font-semibold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{value}</span>
                </span>
              ))}
            </div>
          </Abs>
        );
      })}
      {rows.slice(0, 3).map((row, index) => (row.note ? (
        <span key={`note-${row.status}`} style={abs(L.note(index))} className={`tw-absolute tw-flex tw-flex-col tw-justify-center tw-rounded-xl tw-px-3 tw-text-xs ${row.status === 'APPROVED' ? 'tw-bg-emerald-500/10 tw-text-emerald-900 dark:tw-text-emerald-100' : 'tw-bg-rose-500/10 tw-text-rose-900 dark:tw-text-rose-100'}`}>
          <b className="tw-flex tw-items-center tw-gap-1">{row.status === 'APPROVED' && <LuCheck aria-hidden="true" className="tw-h-3 tw-w-3" />}{label('adminNote')}</b>
          <span className="tw-truncate">{t(`userGuide.stage.${row.note}`)}</span>
        </span>
      ) : null))}
    </>
  );
};

LeaveListPane.propTypes = { top: PropTypes.number.isRequired, state: PropTypes.object.isRequired };
